/**
 * Single source of truth for delivery, returns and alterations copy.
 *
 * Every surface (header ribbon, PDP, cart, checkout, policy pages) must read
 * from here. Before this module existed the same promise was written four
 * different ways — a 500 AZN threshold in the header, "wholesale orders" on the
 * PDP, "complimentary" in the cart — which contradicted each other at the exact
 * moment a shopper decides to pay.
 *
 * The delivery facts below match what the platform actually does:
 * `cart.shipping_total` is always 0 (backend/app/services/cart.py) and checkout
 * offers exactly two methods.
 *
 * NOTE FOR THE BUSINESS OWNER — the values in `NEEDS_CONFIRMATION` are the only
 * ones not derivable from the code. Confirm them and move them into the object
 * above; until then the pages point shoppers at the concierge instead of
 * stating a term the atelier has not agreed to.
 */

export const NEEDS_CONFIRMATION = {
  returnWindowDays: null, // e.g. 14 — legally binding, must come from the owner
  contactEmail: null, // e.g. "concierge@atelierdifalco.az"
  contactPhone: null, // e.g. "+994 12 000 00 00"
  atelierAddress: null, // showroom address for the Contact page
};

export const DELIVERY = {
  /** Shipping is not charged on any order — no minimum, no threshold. */
  isComplimentary: true,
  methods: [
    {
      code: "atelier",
      az: "Atelye kuryeri — Bakı daxili, 48 saat ərzində",
      en: "Atelier courier — within Baku, in 48 hours",
    },
    {
      code: "pickup",
      az: "Atelyedən təhvil — əvvəlcədən görüş təyin etməklə",
      en: "Collect from the atelier — by appointment",
    },
  ],
  ribbon: {
    az: "Bütün sifarişlərdə pulsuz çatdırılma",
    en: "Complimentary delivery on every order",
  },
  short: {
    az: "Pulsuz çatdırılma",
    en: "Complimentary delivery",
  },
};

export const RETURNS = {
  /**
   * The window is legally binding and nobody has confirmed one, so this says
   * what is true — the atelier arranges it — instead of inventing "14 days".
   * Once `NEEDS_CONFIRMATION.returnWindowDays` is filled in, state it here.
   */
  ribbon: {
    az: "Qaytarma atelye ilə razılaşdırılır — konsyerjlə əlaqə saxlayın.",
    en: "Returns are arranged with the atelier — contact the concierge.",
  },
};

export const ALTERATIONS = {
  az: "Ölçü düzəlişləri atelyedə aparılır. Aldığınız parçanı gətirin — dərzimiz siluetinizi dəqiqləşdirsin.",
  en: "Alterations are carried out at the atelier. Bring your piece in and our tailor will refine the silhouette.",
};

/** Pick the right language, falling back to Azerbaijani. */
export const policyText = (entry, locale = "az") =>
  (entry && (entry[locale] || entry.az || entry.en)) || "";
